import toast from "react-hot-toast";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { facilityAdmin } from "../../api";
import { PageHeader, Btn, LoadingSpinner } from "../../components/ui";

export default function DisplayConfigPage() {
  const navigate  = useNavigate();
  const [slug,       setSlug]       = useState(null);
  const [displayToken, setDisplayToken] = useState(null);
  const [zones,      setZones]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [copied,     setCopied]     = useState(null);
  const [screens,    setScreens]    = useState({});
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    Promise.all([
      facilityAdmin.getSettings(),
      facilityAdmin.listZones(),
    ]).then(([sr, zr]) => {
      setSlug(sr.data?.facility_slug || null);
      setDisplayToken(sr.data?.display_token || null);
      setScreens(sr.data?.display_screens || {});
      setZones(zr.data?.results || zr.data || []);
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, []);

  const boardUrl = (zoneId = null) => {
    const base = `${window.location.origin}/display/${slug || "(your-slug)"}`;
    return zoneId ? `${base}?zone=${zoneId}` : base;
  };

  const copy = (url, key) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleRegenerateToken = async () => {
    setRegenerating(true);
    try {
      const res = await facilityAdmin.regenerateDisplayToken();
      setDisplayToken(res.data.display_token);
      toast.success("Display token regenerated.");
    } catch { toast.error("Failed to regenerate token."); }
    finally { setRegenerating(false); }
  };

  const isOnline = (lastSeen) => {
    if (!lastSeen) return false;
    const diff = (Date.now() - new Date(lastSeen).getTime()) / 1000;
    return diff < 180; // online if pinged within 3 minutes
  };

  return (
    <div style={styles.root}>
      <PageHeader
        title="Display Board"
        subtitle="Open these URLs on any TV or monitor. Each board updates live and plays audio announcements."
      />

      {loading ? <LoadingSpinner /> : (
        <>
          {/* Main board URL — all zones */}
          <div style={styles.urlCard}>
            <div style={styles.urlLeft}>
              <span style={{ fontSize: "28px", flexShrink: 0 }}>📺</span>
              <div>
                <div style={styles.urlTitle}>Full Facility Board</div>
                <div style={styles.urlSub}>Shows all zones and counters on one screen</div>
                <div style={styles.urlValue}>{boardUrl()}</div>
              </div>
            </div>
            <div style={styles.urlActions}>
              <Btn onClick={() => window.open(boardUrl(), "_blank")}>Open ↗</Btn>
              <Btn variant="ghost" onClick={() => copy(boardUrl(), "all")}>
                {copied === "all" ? "✓ Copied" : "Copy URL"}
              </Btn>
            </div>
          </div>

          {/* Kiosk URL */}
          <div style={styles.urlCard}>
            <div style={styles.urlLeft}>
              <span style={{ fontSize: "28px", flexShrink: 0 }}>🖥️</span>
              <div>
                <div style={styles.urlTitle}>Kiosk</div>
                <div style={styles.urlSub}>Open this URL on the tablet or touchscreen where patients get their queue numbers</div>
                <div style={styles.urlValue}>{`${window.location.origin}/kiosk/${slug || "(your-slug)"}`}</div>
              </div>
            </div>
            <div style={styles.urlActions}>
              <Btn onClick={() => window.open(`${window.location.origin}/kiosk/${slug || ""}`, "_blank")}>Open ↗</Btn>
              <Btn variant="ghost" onClick={() => copy(`${window.location.origin}/kiosk/${slug || ""}`, "kiosk")}>
                {copied === "kiosk" ? "✓ Copied" : "Copy URL"}
              </Btn>
            </div>
          </div>

          {/* Per-zone board URLs — FIX 6 */}
          {zones.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Zone-Specific Boards</div>
              <p style={styles.sectionNote}>
                Use these URLs to show only one zone on a specific TV — useful when each block or floor has its own screen.
              </p>
              <div style={styles.zoneUrls}>
                {zones.map(zone => (
                  <div key={zone.id} style={styles.zoneUrlRow}>
                    <div style={styles.zoneUrlLeft}>
                      <span style={styles.zoneIcon}>🏢</span>
                      <div>
                        <div style={styles.zoneName}>{zone.name}</div>
                        <div style={styles.zoneUrlValue}>{boardUrl(zone.id)}</div>
                      </div>
                    </div>
                    <div style={styles.urlActions}>
                      <Btn size="sm" onClick={() => window.open(boardUrl(zone.id), "_blank")}>Open ↗</Btn>
                      <Btn size="sm" variant="ghost" onClick={() => copy(boardUrl(zone.id), zone.id)}>
                        {copied === zone.id ? "✓ Copied" : "Copy"}
                      </Btn>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Display Token */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Display Board Token</div>
            <p style={styles.sectionNote}>
              Use the token-based URL for enhanced security. Tokens can be rotated if compromised.
            </p>
            {displayToken && (
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <code style={{ fontSize: "13px", color: "#60A5FA", background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.2)", borderRadius: "6px", padding: "6px 12px", fontFamily: "monospace", flex: 1 }}>
                  {`${window.location.origin}/display/${displayToken}`}
                </code>
                <Btn size="sm" variant="ghost" onClick={() => copy(`${window.location.origin}/display/${displayToken}`, "token")}>
                  {copied === "token" ? "✓ Copied" : "Copy"}
                </Btn>
                <Btn size="sm" variant="ghost" onClick={handleRegenerateToken} disabled={regenerating}>
                  {regenerating ? "Regenerating…" : "↻ Rotate Token"}
                </Btn>
              </div>
            )}
          </div>

          {/* Display Screens (Heartbeat) */}
          {Object.keys(screens).length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Display Screens</div>
              <p style={styles.sectionNote}>Live status of all display boards connected to this facility.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {Object.entries(screens).map(([key, screen]) => {
                  const online = isOnline(screen.last_seen);
                  return (
                    <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "rgba(30,41,59,0.5)", borderRadius: "10px", border: `1px solid ${online ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.1)"}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: online ? "#22C55E" : "#EF4444", flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: "14px", fontWeight: "600", color: "#E2E8F0" }}>{screen.label || screen.zone_name || "Display Board"}</div>
                          <div style={{ fontSize: "11px", color: "#475569" }}>
                            {online ? "Online" : "Offline"} · Last seen: {screen.last_seen ? new Date(screen.last_seen).toLocaleTimeString() : "Never"}
                          </div>
                        </div>
                      </div>
                      <span style={{ fontSize: "11px", fontWeight: "600", color: online ? "#22C55E" : "#EF4444", background: online ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", padding: "3px 10px", borderRadius: "6px" }}>
                        {online ? "● Online" : "● Offline"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Feature summary */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>What the display board shows</div>
            <div style={styles.features}>
              {FEATURES.map(f => (
                <div key={f.title} style={styles.feature}>
                  <span style={{ fontSize: "18px", flexShrink: 0, marginTop: "2px" }}>{f.icon}</span>
                  <div>
                    <div style={styles.featureTitle}>{f.title}</div>
                    <div style={styles.featureDesc}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Config links */}
          <div style={styles.sectionTitle}>Configure the board</div>
          <div style={styles.configCards}>
            {CONFIG_LINKS.map(c => (
              <button key={c.path} onClick={() => navigate(c.path)} style={styles.configCard}>
                <span style={{ fontSize: "22px" }}>{c.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={styles.configTitle}>{c.title}</div>
                  <div style={styles.configDesc}>{c.desc}</div>
                </div>
                <span style={{ color: "#334155", fontSize: "18px" }}>›</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const FEATURES = [
  { icon: "🎫", title: "Currently Serving",    desc: "Ticket number at each active counter" },
  { icon: "👤", title: "Staff Names",           desc: "Optionally shows who is serving at each counter" },
  { icon: "📋", title: "Up Next",               desc: "Next patients waiting, sorted by priority" },
  { icon: "📊", title: "Queue Stats",           desc: "Waiting, serving, and completed today" },
  { icon: "⏱",  title: "Avg Wait Time",         desc: "Estimated wait per station based on recent history" },
  { icon: "📢", title: "Announcement Banner",   desc: "Scrolling notice text (e.g. 'Pharmacy closes at 4PM')" },
  { icon: "🔊", title: "Audio Announcements",   desc: "Chime + ticket number + station name on every call" },
  { icon: "🔁", title: "Live Updates",          desc: "Instant refresh when any ticket is called" },
];

const CONFIG_LINKS = [
  { icon: "⚙️", title: "Board Settings",   desc: "Theme, font size, counters per row, content toggles, audio settings", path: "/admin/settings" },
  { icon: "🔊", title: "Audio Clips",      desc: "Upload chime, digit recordings, and station name clips",               path: "/admin/audio" },
  { icon: "🗂️", title: "Zones & Stations", desc: "Manage counters and stations that appear on the board",                path: "/admin/zones" },
];

const styles = {
  root:         { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "900px" },
  urlCard:      { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "20px", background: "rgba(15,23,42,0.7)", border: "1px solid rgba(37,99,235,0.25)", borderRadius: "16px", padding: "22px", marginBottom: "20px", flexWrap: "wrap" },
  urlLeft:      { display: "flex", gap: "14px", alignItems: "flex-start", flex: 1 },
  urlTitle:     { fontSize: "15px", fontWeight: "700", color: "#E2E8F0", marginBottom: "3px" },
  urlSub:       { fontSize: "12px", color: "#475569", marginBottom: "6px" },
  urlValue:     { fontSize: "12px", color: "#60A5FA", fontFamily: "monospace", wordBreak: "break-all" },
  urlActions:   { display: "flex", gap: "8px", flexShrink: 0, flexWrap: "wrap", alignItems: "flex-start" },
  section:      { background: "rgba(15,23,42,0.5)", border: "1px solid rgba(37,99,235,0.12)", borderRadius: "12px", padding: "18px 20px", marginBottom: "16px" },
  sectionTitle: { fontSize: "11px", fontWeight: "700", color: "#475569", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "12px" },
  sectionNote:  { fontSize: "13px", color: "#64748B", marginBottom: "14px", lineHeight: "1.5", margin: "-4px 0 14px" },
  zoneUrls:     { display: "flex", flexDirection: "column", gap: "10px" },
  zoneUrlRow:   { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", background: "rgba(30,41,59,0.5)", borderRadius: "10px", padding: "12px 16px", flexWrap: "wrap" },
  zoneUrlLeft:  { display: "flex", gap: "12px", alignItems: "flex-start", flex: 1 },
  zoneIcon:     { fontSize: "18px", flexShrink: 0, marginTop: "2px" },
  zoneName:     { fontSize: "14px", fontWeight: "600", color: "#E2E8F0", marginBottom: "3px" },
  zoneUrlValue: { fontSize: "11px", color: "#60A5FA", fontFamily: "monospace", wordBreak: "break-all" },
  features:     { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: "12px" },
  feature:      { display: "flex", gap: "10px", alignItems: "flex-start" },
  featureTitle: { fontSize: "13px", fontWeight: "600", color: "#E2E8F0", marginBottom: "2px" },
  featureDesc:  { fontSize: "12px", color: "#475569", lineHeight: "1.4" },
  configCards:  { display: "flex", flexDirection: "column", gap: "8px" },
  configCard:   { display: "flex", alignItems: "center", gap: "14px", background: "rgba(15,23,42,0.5)", border: "1px solid rgba(37,99,235,0.12)", borderRadius: "10px", padding: "14px 18px", cursor: "pointer", textAlign: "left", width: "100%", fontFamily: "inherit" },
  configTitle:  { fontSize: "14px", fontWeight: "600", color: "#E2E8F0", marginBottom: "2px" },
  configDesc:   { fontSize: "12px", color: "#475569" },
};
